import { useEffect, useState } from "react";
import { useStore } from "react-redux";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import type { RootState } from "src/redux/store";
import {
  regenerateS3AttachmentsId,
  resetPreScreen,
  setResolveSchemaInput,
  type ResolveSchemaInput,
} from "./jsonFormsSlice";
import { userHasLobAccess } from "./preScreen/lobAccess";
import { LINE_OF_BUSINESS_BY_LOB } from "./preScreen/lobConfigs";
import type { LobKey } from "./preScreen/types";

/** URL query parameter that opens the form on an existing claim's next stage. */
export const CLAIM_NUMBER_QUERY_PARAM = "claimNumber";

/** Reads the trimmed `claimNumber` query parameter, or `null` when absent or blank. */
export function readClaimNumber(searchParams: URLSearchParams): string | null {
  return searchParams.get(CLAIM_NUMBER_QUERY_PARAM)?.trim() || null;
}

interface UseClaimStageSchemaFetchArgs {
  /** `claimNumber` read from the URL, or `null` when absent. */
  claimNumber: string | null;
  /** LOB from the URL, or `null` when absent or unrecognized. */
  lobKey: LobKey | null;
  /** Orchestrator's abort-aware fetch dispatcher. */
  dispatchFetch: (input: ResolveSchemaInput) => void;
}

/**
 * Drives the stage-by-stage flow: when the form is opened with
 * `?claimNumber=` the pre-screen is skipped and `/resolveSchema` is called
 * with `{ lineOfBusiness, claimNumber }` so the service returns the schema
 * for that claim's next stage.
 *
 * - A persisted input for the same LOB and claim (page reload mid-stage) is
 *   re-fetched without rotating `s3AttachmentsId`, so files already uploaded
 *   for this stage stay attached.
 * - Any other persisted state (different claim, a pre-screen input, or a
 *   completed submission) is reset and a fresh fetch starts with a new
 *   attachments id.
 *
 * State is read from the store at effect time (not a render snapshot) so a
 * second run for the same claim — React.StrictMode double-invokes effects in
 * dev — sees the in-flight request and does nothing instead of resetting the
 * slice and firing a duplicate fetch.
 *
 * Does nothing while auth is loading, without a claim number, or when the
 * user lacks access to the LOB (the orchestrator renders access-denied).
 *
 * @returns `true` once the fetch for the current `claimNumber` has been
 *   started in this mount. The orchestrator uses it to tell "about to fetch"
 *   (show a spinner) apart from "the user cleared the claim-stage state"
 *   (drop the query parameter and fall back to the pre-screen).
 */
export function useClaimStageSchemaFetch({
  claimNumber,
  lobKey,
  dispatchFetch,
}: UseClaimStageSchemaFetchArgs): boolean {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const { user, authenticating } = useAppSelector((state) => state.auth);
  const [startedFor, setStartedFor] = useState<string | null>(null);

  useEffect(() => {
    if (authenticating || !claimNumber || !lobKey || !userHasLobAccess(user.roles, lobKey)) {
      return;
    }
    setStartedFor(claimNumber);

    const lineOfBusiness = LINE_OF_BUSINESS_BY_LOB[lobKey];
    const { preScreen } = store.getState().jsonForms;
    const persisted = preScreen.resolveSchemaInput;
    const isSameClaim =
      !preScreen.submissionCompleted &&
      persisted?.lineOfBusiness === lineOfBusiness &&
      persisted.claimNumber === claimNumber;

    if (isSameClaim) {
      if (!preScreen.fetchedSchema && preScreen.schemaFetchStatus === "idle") {
        dispatchFetch(persisted);
      }
      return;
    }

    const input: ResolveSchemaInput = { lineOfBusiness, claimNumber };
    dispatch(resetPreScreen());
    dispatch(setResolveSchemaInput(input));
    dispatch(regenerateS3AttachmentsId());
    dispatchFetch(input);
    // Re-run only when auth settles or the URL's LOB / claim changes.
    // eslint-disable-next-line
  }, [authenticating, lobKey, claimNumber]);

  return claimNumber !== null && startedFor === claimNumber;
}
